import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Check, Loader2, ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { authStorage } from '@/utils/authStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/billing`;

export default function BillingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [sub, setSub] = useState(null);
  const [interval, setInterval] = useState('monthly');
  const [busy, setBusy] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState(null);
  const [cfg, setCfg] = useState(null);
  const user = authStorage.getAuthorUser();

  const load = useCallback(async () => {
    const [p, s, c] = await Promise.all([
      axios.get(`${API}/plans`),
      user ? axios.get(`${API}/subscription`, { params: { user_id: user.id } }) : Promise.resolve({ data: null }),
      axios.get(`${API}/config`).catch(() => ({ data: null })),
    ]);
    setCfg(c.data);
    setPlans(p.data.plans);
    setPacks(p.data.packs || []);
    setSub(s.data);
  }, [user]);

  useEffect(() => {
    if (!user) { navigate('/thesis/login'); return; }
    load().catch(() => toast.error('Could not load the plans'));
    if (new URLSearchParams(window.location.search).get('checkout') === 'done') {
      toast.success('Payment received — your plan activates as soon as Mollie confirms it.');
    }
  }, [load, navigate, user]);

  const subscribe = async (planKey) => {
    setBusy(planKey);
    try {
      const { data } = await axios.post(`${API}/checkout`,
        { plan: planKey, interval, promo_code: promo?.code || promoInput.trim() || null },
        { params: { user_id: user.id } });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || 'Checkout failed');
      setBusy('');
    }
  };

  const buyPack = async (packKey) => {
    setBusy(packKey);
    try {
      const { data } = await axios.post(`${API}/checkout-pack`, { pack: packKey },
        { params: { user_id: user.id } });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || 'Checkout failed');
      setBusy('');
    }
  };

  const changePlan = async (planKey) => {
    setBusy(planKey);
    try {
      await axios.post(`${API}/change-plan`, { plan: planKey, interval },
        { params: { user_id: user.id } });
      toast.success('Plan switched — the next invoice uses the new price');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || 'Could not switch plan');
    } finally {
      setBusy('');
    }
  };

  const applyPromo = async (planKey) => {
    if (!promoInput.trim()) { setPromo(null); return; }
    try {
      const { data } = await axios.post(`${API}/promo-codes/validate`,
        { code: promoInput.trim(), plan: planKey || 'researcher', interval },
        { params: { user_id: user.id } });
      if (!data.valid) { setPromo(null); toast.error(data.reason); return; }
      setPromo(data);
      toast.success(`${data.code}: ${data.label} — €${data.new_amount} instead of €${data.original_amount}`);
    } catch (e) {
      setPromo(null);
      toast.error(e?.response?.data?.detail || 'Could not check that code');
    }
  };

  const cancel = async () => {    setBusy('cancel');
    try {
      await axios.post(`${API}/cancel`, {}, { params: { user_id: user.id } });
      toast.success('Subscription cancelled — access stays until the end of the paid period');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="w-full min-w-0 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/thesis')} data-testid="billing-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to projects
          </Button>
        </div>

        <div className="space-y-2">
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            Plans & billing
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl">
            Evidence-grounded writing infrastructure, priced per researcher. Yearly billing costs
            ten months — two months free. Payments are processed by Mollie (card, iDEAL,
            Bancontact, SEPA, Apple Pay). VAT is applied under EU OSS rules; businesses with a
            VAT number can request a reverse-charge invoice.{' '}
            <a href="/legal/billing" className="underline hover:text-foreground"
              data-testid="billing-terms-link">Billing, invoices &amp; right of withdrawal</a>
          </p>
          {cfg && cfg.mode !== 'live' && (
            <div className="p-3 rounded-lg border border-amber-500/50 bg-amber-500/[0.06] text-[12px]"
              data-testid="billing-mode-banner">
              <strong>Test mode.</strong> Payments are running against the Mollie test API — no money
              moves. Add a live API key (MOLLIE_API_KEY) to accept real payments.
            </div>
          )}
          {cfg && cfg.mode === 'live' && (
            <div className="p-3 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.05] text-[12px]"
              data-testid="billing-mode-banner">
              <strong>Live payments are enabled.</strong> Webhook: <code className="text-[11px] break-all">{cfg.webhook_url}</code>
            </div>
          )}
        </div>

        {sub && (
          <Card data-testid="billing-current-plan">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base md:text-lg flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />Current plan: {sub.plan_name}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {sub.state === 'trialing' && sub.trial?.days_left != null ? (
                      <span className="text-amber-700 dark:text-amber-300 font-medium">
                        Free trial · {sub.trial.days_left} days left
                        {sub.trial.ends_at ? ` (until ${String(sub.trial.ends_at).slice(0, 10)})` : ''} · full Doctoral access
                      </span>
                    ) : (
                      <>Status {sub.state}
                        {sub.next_payment_at ? ` · renews ${String(sub.next_payment_at).slice(0, 10)}` : ''}</>
                    )}
                    {' · '}
                    {sub.usage.projects} project(s) · {sub.usage.documents} source(s) ·{' '}
                    {sub.usage.generations_per_month || 0}
                    {sub.limits?.generations_per_month >= 0 ? `/${sub.limits.generations_per_month}` : ''}
                    {' generation(s) this month'}
                  </CardDescription>
                </div>
                {sub.mollie_subscription_id && sub.state === 'active' && (
                  <Button variant="outline" size="sm" onClick={cancel} disabled={busy === 'cancel'}
                    data-testid="billing-cancel-btn">
                    {busy === 'cancel' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel subscription'}
                  </Button>
                )}
              </div>
            </CardHeader>
          </Card>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2" data-testid="billing-interval-toggle">
          {['monthly', 'yearly'].map((iv) => (
            <Button key={iv} variant={interval === iv ? 'default' : 'outline'} size="sm"
              onClick={() => setInterval(iv)} data-testid={`billing-interval-${iv}`}>
              {iv === 'monthly' ? 'Monthly' : 'Yearly · save 17%'}
            </Button>
          ))}
          </div>
          <div className="flex items-center gap-2" data-testid="billing-promo">
            <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
              placeholder="Promo code" className="h-9 w-40 uppercase"
              data-testid="billing-promo-input" />
            <Button size="sm" variant="outline" className="rounded-full"
              onClick={() => applyPromo(sub?.plan && sub.plan !== 'free' ? sub.plan : 'researcher')}
              data-testid="billing-promo-apply">Apply</Button>
            {promo && (
              <span className="text-[12px] font-semibold text-emerald-700 dark:text-emerald-400"
                data-testid="billing-promo-applied">
                {promo.code} · {promo.label}
                {promo.apply_to === 'all' ? ' (every payment)' : ' (first payment)'}
              </span>
            )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((p) => {
            const isCurrent = sub?.plan === p.key;
            const canSwitch = !!sub?.mollie_subscription_id && sub?.state === 'active' && !isCurrent && p.key !== 'free';
            const price = interval === 'monthly' ? p.monthly : p.yearly;
            return (
              <Card key={p.key} className={p.badge ? 'border-primary/50 shadow-lg' : ''}
                data-testid={`billing-plan-${p.key}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base">{p.name}</CardTitle>
                    {p.badge && <Badge className="text-[10px]">{p.badge}</Badge>}
                  </div>
                  <div className="mt-2">
                    <span className="text-3xl font-semibold" data-testid={`billing-price-${p.key}`}>
                      €{promo && p.key !== 'free'
                        ? (parseFloat(price) * (1 - promo.discount_pct / 100)).toFixed(2)
                        : price}
                    </span>
                    {promo && p.key !== 'free' && (
                      <span className="text-sm line-through text-muted-foreground ml-2">€{price}</span>
                    )}
                    <span className="text-xs text-muted-foreground ml-1">
                      {p.key === 'free' ? 'forever' : interval === 'monthly' ? '/ month' : '/ year'}
                    </span>
                  </div>
                  {interval === 'yearly' && p.key !== 'free' && (
                    <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 mt-1"
                      data-testid={`billing-savings-${p.key}`}>
                      You save €{Math.round(parseFloat(p.monthly) * 12 - parseFloat(p.yearly))} a year
                      · 2 months free
                    </p>
                  )}
                  {interval === 'yearly' && p.key !== 'free' && (
                    <p className="text-[11px] text-muted-foreground">
                      €{(parseFloat(p.yearly) / 12).toFixed(2)} / month billed annually
                      (instead of €{p.monthly})
                    </p>
                  )}
                  <CardDescription className="mt-1">{p.tagline}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ul className="space-y-1.5">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-3.5 h-3.5 mt-1 text-primary shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full" disabled={isCurrent || p.key === 'free' || busy === p.key}
                    variant={p.badge ? 'default' : 'outline'}
                    onClick={() => (canSwitch ? changePlan(p.key) : subscribe(p.key))}
                    data-testid={`billing-subscribe-${p.key}`}>
                    {busy === p.key ? <Loader2 className="w-4 h-4 animate-spin" />
                      : isCurrent ? 'Your plan'
                        : p.key === 'free' ? 'Included'
                          : canSwitch ? `Switch to ${p.name}` : `Choose ${p.name}`}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {packs.map((pk) => (
          <Card key={pk.key} data-testid={`billing-pack-${pk.key}`}>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">{pk.name}</CardTitle>
                <Badge variant="outline" className="text-[10px]">one-off payment</Badge>
              </div>
              <div className="mt-2">
                <span className="text-3xl font-semibold" data-testid={`billing-pack-price-${pk.key}`}>€{pk.price}</span>
                <span className="text-xs text-muted-foreground ml-1">once · {pk.months} months</span>
              </div>
              <CardDescription className="mt-1">{pk.tagline}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {(pk.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Check className="w-3.5 h-3.5 mt-1 text-primary shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button className="w-full sm:w-auto" disabled={busy === pk.key}
                onClick={() => buyPack(pk.key)} data-testid={`billing-buy-pack-${pk.key}`}>
                {busy === pk.key ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy ${pk.name}`}
              </Button>
            </CardContent>
          </Card>
        ))}

        <p className="text-xs text-muted-foreground max-w-3xl">
          Test mode is active: Mollie's checkout lets you simulate a paid, failed or expired payment.
          Your plan is activated only after Mollie's webhook confirms the payment, never on the
          browser redirect. Cancelling stops future charges; existing content is never deleted.
        </p>
      </div>
    </div>
  );
}
